<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps<{
  imageUrl: string
}>()

const emit = defineEmits<{
  (e: 'crop', blob: Blob): void
  (e: 'cancel'): void
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)

const CROP_SIZE = 320
const VIEW_SIZE = 300

const img = new Image()
let scale = 1
let offsetX = 0
let offsetY = 0
let dragging = false
let dragStartX = 0
let dragStartY = 0
let startOffsetX = 0
let startOffsetY = 0

const draw = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')!

  ctx.clearRect(0, 0, VIEW_SIZE, VIEW_SIZE)

  // Draw image
  const w = img.width * scale
  const h = img.height * scale
  ctx.drawImage(img, offsetX, offsetY, w, h)

  // Draw dark overlay with circular cutout
  ctx.save()
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
  ctx.fillRect(0, 0, VIEW_SIZE, VIEW_SIZE)
  ctx.globalCompositeOperation = 'destination-out'
  ctx.beginPath()
  ctx.arc(VIEW_SIZE / 2, VIEW_SIZE / 2, VIEW_SIZE / 2 - 10, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  // Draw circle border
  ctx.strokeStyle = 'white'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(VIEW_SIZE / 2, VIEW_SIZE / 2, VIEW_SIZE / 2 - 10, 0, Math.PI * 2)
  ctx.stroke()
}

const fitImage = () => {
  const minDim = Math.min(img.width, img.height)
  scale = VIEW_SIZE / minDim
  offsetX = (VIEW_SIZE - img.width * scale) / 2
  offsetY = (VIEW_SIZE - img.height * scale) / 2
  draw()
}

onMounted(() => {
  img.onload = fitImage
  img.src = props.imageUrl

  const canvas = canvasRef.value!

  const onPointerDown = (e: PointerEvent) => {
    dragging = true
    dragStartX = e.clientX
    dragStartY = e.clientY
    startOffsetX = offsetX
    startOffsetY = offsetY
    canvas.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: PointerEvent) => {
    if (!dragging) return
    offsetX = startOffsetX + (e.clientX - dragStartX)
    offsetY = startOffsetY + (e.clientY - dragStartY)
    draw()
  }

  const onPointerUp = () => {
    dragging = false
  }

  const onWheel = (e: WheelEvent) => {
    e.preventDefault()
    const centerX = VIEW_SIZE / 2
    const centerY = VIEW_SIZE / 2
    const oldScale = scale
    const factor = e.deltaY > 0 ? 0.95 : 1.05
    scale *= factor

    // Zoom toward center
    offsetX = centerX - (centerX - offsetX) * (scale / oldScale)
    offsetY = centerY - (centerY - offsetY) * (scale / oldScale)
    draw()
  }

  canvas.addEventListener('pointerdown', onPointerDown)
  canvas.addEventListener('pointermove', onPointerMove)
  canvas.addEventListener('pointerup', onPointerUp)
  canvas.addEventListener('wheel', onWheel, { passive: false })

  cleanupFns.push(() => {
    canvas.removeEventListener('pointerdown', onPointerDown)
    canvas.removeEventListener('pointermove', onPointerMove)
    canvas.removeEventListener('pointerup', onPointerUp)
    canvas.removeEventListener('wheel', onWheel)
  })
})

const cleanupFns: (() => void)[] = []
onUnmounted(() => cleanupFns.forEach(fn => fn()))

const handleCrop = () => {
  const cropCanvas = document.createElement('canvas')
  cropCanvas.width = CROP_SIZE
  cropCanvas.height = CROP_SIZE
  const ctx = cropCanvas.getContext('2d')!

  // Clip to circle
  ctx.beginPath()
  ctx.arc(CROP_SIZE / 2, CROP_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2)
  ctx.clip()

  // Scale factor from view to crop
  const cropScale = CROP_SIZE / VIEW_SIZE
  const radius = VIEW_SIZE / 2 - 10
  const cropRadius = radius * cropScale

  // Map the visible circle area to the crop canvas
  const srcCenterX = VIEW_SIZE / 2
  const srcCenterY = VIEW_SIZE / 2
  const srcLeft = srcCenterX - radius
  const srcTop = srcCenterY - radius

  const imgX = (offsetX - srcLeft) * cropScale
  const imgY = (offsetY - srcTop) * cropScale
  const imgW = img.width * scale * cropScale
  const imgH = img.height * scale * cropScale

  // Offset to center the crop circle in the canvas
  const canvasOffsetX = (CROP_SIZE - cropRadius * 2) / 2
  const canvasOffsetY = (CROP_SIZE - cropRadius * 2) / 2

  ctx.drawImage(img, imgX + canvasOffsetX, imgY + canvasOffsetY, imgW, imgH)

  cropCanvas.toBlob((blob) => {
    if (blob) emit('crop', blob)
  }, 'image/jpeg', 0.9)
}
</script>

<template>
  <div class="flex flex-col items-center gap-4">
    <p class="text-sm text-slate-500">Drag to position, scroll to zoom</p>
    <div class="relative" :style="{ width: VIEW_SIZE + 'px', height: VIEW_SIZE + 'px' }">
      <canvas
        ref="canvasRef"
        :width="VIEW_SIZE"
        :height="VIEW_SIZE"
        class="cursor-move rounded-lg"
        style="touch-action: none"
      />
    </div>
    <div class="flex gap-3">
      <Button label="Cancel" severity="secondary" text @click="emit('cancel')" />
      <Button label="Save Avatar" severity="primary" @click="handleCrop" />
    </div>
  </div>
</template>
